import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { AuthCodeData } from 'src/app/model/api/auth-code-data';
import { environment } from 'src/environments/environment';
import { resources, utilities } from './api';
import { DataRest, IdDataRest } from '../model/api/data-rest';
import { User } from '../model/user';
import { TokenData } from '../model/api/token-data';
import { LOCALSTORAGE_CODE_VERIFIER } from '../conf/user';
import jwt_decode from 'jwt-decode';
import { crypt } from './crypt';
import { MY_FORMATS } from '../conf/date_inputs';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    constructor(
        private http: HttpClient,
    ) { }

    signUpUser(user: User, technicianId?: number, verification?: string) { // user signup
        let params = new HttpParams();
        if (technicianId) {
            params = params.set('technicianId', technicianId.toString());
        }
        if (verification) {
            params = params.set('verification', verification);
        }
        return this.http.post<IdDataRest>(`${environment.apiUrl}${resources.get('oauth')}${resources.get('signup')}${resources.get('user')}`, user, { params });
    }

    signUpTechnician(user: User) { // user signup as technician
        return this.http.post<IdDataRest>(`${environment.apiUrl}${resources.get('oauth')}${resources.get('signup')}${resources.get('technician')}`, user);
    }

    authCode(username: string, password: string): Observable<AuthCodeData> {
        const queryParams = new HttpParams()
            .set('client_id', environment.clientId)
            .set('response_type', 'code')
            .set('redirect_uri', location.protocol + '//' + location.host + '/role')
            .set('state', utilities.randomString(100))
            .set('nonce', utilities.randomString(100))
            .set('code_challenge', this.getCodeChallenge())
            .set('code_challenge_method', 'S256');
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
                Authorization: 'Basic ' + this.encClientId()
            }),
            params: queryParams
        };

        return this.http.post<AuthCodeData>(
            `${environment.apiUrl}${resources.get('oauth')}${resources.get('authCode')}`,
            // eslint-disable-next-line max-len
            `grant_type=authorization_code&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&client_id=${encodeURIComponent(environment.clientId)}`,
            httpOptions
        );
    }

    getToken(code: string): Observable<TokenData> {
        let codeVerifier = localStorage.getItem(LOCALSTORAGE_CODE_VERIFIER);
        if (!codeVerifier) {
            codeVerifier = '';
        }
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
                Authorization: 'Basic ' + this.encClientId()
            })
        };
        const params = new HttpParams()
            .set('grant_type', 'authorization_code')
            .set('code', code)
            .set('redirect_uri', `${location.protocol}//${location.host}/role`)
            .set('code_verifier', codeVerifier);
        return this.http.post<TokenData>(
            `${environment.apiUrl}${resources.get('oauth')}${resources.get('token')}`,
            params,
            httpOptions
        );
    }


    //----------------- NEW 2FA Auth



    authMfaCode(username: string, password: string, mfaCode?: string): Observable<AuthCodeData> {
        const queryParams = new HttpParams()
            .set('client_id', environment.clientId)
            .set('response_type', 'code')
            .set('redirect_uri', location.protocol + '//' + location.host + '/role')
            .set('state', utilities.randomString(100))
            .set('nonce', utilities.randomString(100))
            .set('code_challenge', this.getCodeChallenge())
            .set('code_challenge_method', 'S256');
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
                Authorization: 'Basic ' + this.encClientId()
            }),
            params: queryParams
        };
        let mfaCodeStr = ``;
        if (mfaCode !== undefined && mfaCode.length > 0) {
            mfaCodeStr = `&mfa_code=` + mfaCode;
        }
        return this.http.post<AuthCodeData>(
            `${environment.apiUrl}${resources.get('oauth')}${resources.get('mfa')}${resources.get('authCode')}`,
            // eslint-disable-next-line max-len
            `grant_type=authorization_code&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&client_id=${encodeURIComponent(environment.clientId)}` + mfaCodeStr,
            httpOptions
        );
    }

    getMfaToken(code: string): Observable<TokenData> {
        let codeVerifier = localStorage.getItem(LOCALSTORAGE_CODE_VERIFIER);
        if (!codeVerifier) {
            codeVerifier = '';
        }
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
                Authorization: 'Basic ' + this.encClientId()
            })
        };
        const params = new HttpParams()
            .set('grant_type', 'authorization_code')
            .set('code', code)
            .set('redirect_uri', `${location.protocol}//${location.host}/role`)
            .set('code_verifier', codeVerifier);
        return this.http.post<TokenData>(
            `${environment.apiUrl}${resources.get('oauth')}${resources.get('mfa')}${resources.get('token')}`,
            params,
            httpOptions
        );
    }

    //----------------- NEW 2FA Auth

    resetPassword(email: string | number | boolean) {
        let params = new HttpParams();
        params = params.set('email', email);
        return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('oauth')}${resources.get('password')}${resources.get('reset')}`, {}, { params });
    }

    updatePassword(email: string, newPassword: string, verification: string, time: number) {
        // eslint-disable-next-line max-len
        const queryParams = `email=${encodeURIComponent(email)}&verification=${encodeURIComponent(verification)}&pwd=${encodeURIComponent(newPassword)}&time=${encodeURIComponent(time.toString())}`;
        return this.http.post<DataRest>(
            `${environment.apiUrl}${resources.get('oauth')}${resources.get('password')}${resources.get('update')}?${queryParams}`,
            {},
            {}
        );
    }

    encClientId(): string {
        const client = environment.clientId + ':' + environment.clientSecret;
        return btoa(client);
    }

    getCodeChallenge(): string {
        const codeVerifier = utilities.randomString(100);
        localStorage.setItem(LOCALSTORAGE_CODE_VERIFIER, codeVerifier);
        return crypt.base64VerifierHash(codeVerifier);
    }

    getTokenExpirationDate(token: string): Date | null {
        const decoded: any = jwt_decode(token);
        if (decoded.exp === undefined) {
            return null;
        }
        const date = new Date(0);
        date.setUTCSeconds(decoded.exp);
        return date;
    }

    getTokenUserId(token: string): number {
        const decoded: any = jwt_decode(token);
        return decoded.userid;
    }

    getTokenPermissions(token: string): string {
        const decoded: any = jwt_decode(token);
        return decoded.permissions;
    }

    isTokenValid(token?: string): boolean {
        if (!token) {
            return false;
        }
        const date = this.getTokenExpirationDate(token);
        if (!date) {
            return false;
        }
        const currentDate = new Date().valueOf();
        return date.valueOf() > currentDate;
    }

    confirmEmail(id: number, data: string): Observable<DataRest> {
        let params = new HttpParams();
        if (data) {
            params = params.set('data', data);
        }
        return this.http.post<DataRest>(
            `${environment.apiUrl}${resources.get('oauth')}${resources.get('confirm')}${resources.get('email')}/${id}`,
            {},
            { params }
        );
    }

}
